import cn from 'classnames';
import { Link } from 'react-router';
import { ClubSocialLinks, SocialNetworksFormData } from './contracts';
import { useSocialNetworksQuery } from '@data/socialNetworks/useSocialNetworksQuery';
import { Loader } from '@ui/common/components/Loader/Loader';
import { SocialNetworkType } from '@repo/types';
import { SocialGlyph } from './socialGlyphs';

// Accessible name for the link that wraps each (aria-hidden) brand glyph.
const networkAlt: Record<SocialNetworkType, string> = {
  [SocialNetworkType.Facebook]: 'Facebook',
  [SocialNetworkType.Twitter]: 'X (Twitter)',
  [SocialNetworkType.Instagram]: 'Instagram',
  [SocialNetworkType.YouTube]: 'YouTube',
  [SocialNetworkType.TikTok]: 'TikTok',
  [SocialNetworkType.Threads]: 'Threads',
  [SocialNetworkType.WhatsApp]: 'WhatsApp',
  [SocialNetworkType.Bluesky]: 'Bluesky',
};

// --- Club "socials" prop mode -----------------------------------------------
// A club-supplied link (Club Info tab "Follow" block) skips the tenant fetch
// entirely. Flickr has no SocialNetworkType entry (and none is added — the
// persisted enum stays as-is), so it's drawn as a local inline glyph instead
// of going through the shared brand-glyph set like the other four.

const CLUB_SOCIAL_ORDER: (keyof ClubSocialLinks)[] = ['facebook', 'twitter', 'instagram', 'youtube', 'flickr'];

const clubSocialAlt: Record<keyof ClubSocialLinks, string> = {
  facebook: 'Facebook',
  twitter: 'X (Twitter)',
  instagram: 'Instagram',
  youtube: 'YouTube',
  flickr: 'Flickr',
};

const clubSocialNetwork: Partial<Record<keyof ClubSocialLinks, SocialNetworkType>> = {
  facebook: SocialNetworkType.Facebook,
  twitter: SocialNetworkType.Twitter,
  instagram: SocialNetworkType.Instagram,
  youtube: SocialNetworkType.YouTube,
};

function FlickrIcon() {
  return (
    <svg
      className="social-networks__glyph social-networks__glyph--flickr"
      width="26"
      height="26"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <circle cx="7.5" cy="12" r="4.3" fill="#0063dc" />
      <circle cx="16.5" cy="12" r="4.3" fill="#ff0084" />
    </svg>
  );
}

// Tenant social data is stored as full URLs; a club's `socials.twitter` value
// may instead be a bare handle (e.g. "@club") — normalise it the same way.
function normaliseTwitterUrl(value: string): string {
  const trimmed = value.trim();
  if (/^https?:\/\//i.test(trimmed)) {
    return trimmed;
  }
  return `https://x.com/${trimmed.replace(/^@/, '')}`;
}

// Facebook/Instagram/YouTube/Flickr club-supplied URLs have no scheme validation at the API
// boundary — guard against a stored non-http(s) scheme (e.g. `javascript:`) becoming a
// clickable script-execution link, mirroring normaliseTwitterUrl's scheme handling above.
function normaliseExternalUrl(value: string): string {
  const trimmed = value.trim();
  if (/^https?:\/\//i.test(trimmed)) {
    return trimmed;
  }
  return `https://${trimmed}`;
}

function clubSocialHref(key: keyof ClubSocialLinks, value: string): string {
  return key === 'twitter' ? normaliseTwitterUrl(value) : normaliseExternalUrl(value);
}

function hasAnyClubSocial(socials?: ClubSocialLinks): boolean {
  return !!socials && CLUB_SOCIAL_ORDER.some((key) => !!socials[key]?.trim());
}

type ClubSocialItemProps = { socialKey: keyof ClubSocialLinks; value: string };

function ClubSocialItem({ socialKey, value }: ClubSocialItemProps) {
  const network = clubSocialNetwork[socialKey];

  return (
    <li>
      <Link to={clubSocialHref(socialKey, value)} target="_blank" aria-label={clubSocialAlt[socialKey]}>
        {network ? <SocialGlyph network={network} /> : <FlickrIcon />}
      </Link>
    </li>
  );
}

type ClubSocialListProps = { socials: ClubSocialLinks; className?: string };

function ClubSocialList({ socials, className }: ClubSocialListProps) {
  return (
    <ul className={cn('social-networks', className)}>
      {CLUB_SOCIAL_ORDER.filter((key) => !!socials[key]?.trim()).map((key) => (
        <ClubSocialItem key={key} socialKey={key} value={socials[key] as string} />
      ))}
    </ul>
  );
}

export type SocialNetworksProps = SocialNetworksFormData & {
  /** Club Info tab "Follow" block — when any value is non-empty, renders these
   * links instead of the tenant-wide social network list (which is not fetched). */
  socials?: ClubSocialLinks;
  className?: string;
};

export function SocialNetworks(props: SocialNetworksProps) {
  const { socials, className } = props;
  const { data, isLoading, error } = useSocialNetworksQuery({ enabled: !hasAnyClubSocial(socials) });

  if (hasAnyClubSocial(socials)) {
    return <ClubSocialList socials={socials as ClubSocialLinks} className={className} />;
  }

  if (isLoading) {
    return <Loader />;
  }

  if (error || !data || data.length === 0) {
    return null;
  }

  return (
    <ul className={cn('social-networks', className)}>
      {data.map((socialNetwork) => {
        const alt = networkAlt[socialNetwork.networkType];
        if (!alt) {
          return null;
        }

        return (
          <li key={socialNetwork.id}>
            <Link to={socialNetwork.url} target="_blank" aria-label={alt}>
              <SocialGlyph network={socialNetwork.networkType} />
            </Link>
          </li>
        );
      })}
    </ul>
  );
}
